import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import StatusBadge from "@/components/app/StatusBadge";
import AssetDrawer from "@/components/app/AssetDrawer";
import RecordConversation from "@/components/app/RecordConversation";
import MobileFilterSheet from "@/components/app/MobileFilterSheet";
import { ASSET } from "@/constants/testIds";
import AssigneeTypeahead from "@/components/app/AssigneeTypeahead";
import { Search, Filter, ChevronLeft, ChevronRight, Rocket, MoreHorizontal, Undo2, Trash2, X, PlusCircle, Eye, Pencil, History, Upload, Download, Power } from "lucide-react";
import CrudDialog, { ConfirmDeleteDialog } from "@/components/app/CrudDialog";
import BulkAssetUploadDialog from "@/components/app/BulkAssetUploadDialog";
import { useAuth } from "@/context/AuthContext";
import { ScheduleWhatsAppButton } from "@/components/whatsapp/ScheduleWhatsAppButton";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";

const LIFECYCLE_STATES = ["all", "in_use", "in_warehouse", "reserved", "in_transit", "packed", "under_repair", "awaiting_inspection", "disposed", "deactivated"];
const CONDITIONS = ["all", "A", "B", "C", "D", "Scrap"];

export default function Assets() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(sp.get("q") || "");
  const [lifecycle, setLifecycle] = useState(sp.get("lifecycle_state") || "all");
  const [condition, setCondition] = useState(sp.get("condition") || "all");
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [newOpen, setNewOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [masters, setMasters] = useState([]);
  const [topHit, setTopHit] = useState(null);
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    api.get("/master-companies").then(({ data }) => setMasters(data.items || []));
    api.get("/companies", { params: { limit: 1000 } }).then(({ data }) => setCompanies(data.items || []));
  }, []);

  // When the search text looks like a serial/tag, hit the fast search
  // endpoint too so the exact device jumps to the top of the table.
  useEffect(() => {
    const trimmed = (q || "").trim();
    if (!trimmed || trimmed.length < 3 || !/^[A-Za-z0-9-]{3,}$/.test(trimmed)) {
      setTopHit(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/asset-search/by-serial",
          { params: { q: trimmed, limit: 3 } });
        if (!cancelled && (data.kind === "exact" || data.kind === "duplicates")) {
          setTopHit(data);
        } else if (!cancelled) {
          setTopHit(null);
        }
      } catch {
        if (!cancelled) setTopHit(null);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const params = useMemo(() => {
    const p = { limit, skip };
    if (q) p.q = q;
    if (lifecycle && lifecycle !== "all") p.lifecycle_state = lifecycle;
    if (condition && condition !== "all") p.condition = condition;
    return p;
  }, [q, lifecycle, condition, skip, limit]);

  const load = () => {
    setLoading(true);
    api.get("/assets/", { params })
      .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [params]);

  const applyFilters = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (lifecycle && lifecycle !== "all") next.set("lifecycle_state", lifecycle);
    if (condition && condition !== "all") next.set("condition", condition);
    setSp(next);
    setSkip(0);
    setSelected(new Set());
  };

  const clearFilters = () => {
    setQ(""); setLifecycle("all"); setCondition("all");
    setSp(new URLSearchParams()); setSkip(0); setSelected(new Set());
  };

  const openAsset = (id) => { setSelectedId(id); setDrawerOpen(true); };

  const toggleRow = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (rows.every((r) => s.has(r.id))) {
        const next = new Set(s);
        rows.forEach((r) => next.delete(r.id));
        return next;
      }
      const next = new Set(s);
      rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (action === "deactivate") {
      const reason = window.prompt(`Deactivate ${ids.length} asset(s)? Optional reason:`, "");
      if (reason === null) return;
      let ok = 0;
      for (const id of ids) {
        try { await api.post(`/assets/${id}/status`, { active: false, reason }); ok += 1; } catch { /* skip */ }
      }
      toast.success(`Deactivated ${ok} asset(s)`);
      setSelected(new Set()); load(); return;
    }
    try {
      const { data } = await api.post("/assets/bulk", { action, asset_ids: ids });
      toast.success(`${action}: applied to ${data.applied}${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    }
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Lifecycle · Assets</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Asset Registry</h1>
          <p className="text-sm text-slate-500 mt-0.5">Golden asset records for every technology asset across every lifecycle.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <RecordConversation contextType="general" contextLabel="Asset Registry" />
          {!user?.master_company_id && <ScheduleWhatsAppButton kind="allocation" ids={Array.from(selected)} />}
          {!user?.master_company_id && (
            <Button variant="outline" onClick={async () => {
              const params = new URLSearchParams();
              if (q) params.set("q", q);
              if (lifecycle && lifecycle !== "all") params.set("lifecycle_state", lifecycle);
              if (condition && condition !== "all") params.set("condition", condition);
              const url = process.env.REACT_APP_BACKEND_URL
                + "/api/assets/export.csv" + (params.toString() ? "?" + params : "");
              try {
                const r = await fetch(url, { credentials: "include" });
                if (r.status === 403) { toast.error("Export is restricted to Suntronix admins."); return; }
                if (!r.ok) throw new Error("Export failed");
                const rows = r.headers.get("X-Row-Count") || "?";
                const b = await r.blob();
                const u = URL.createObjectURL(b);
                const a = document.createElement("a");
                a.href = u;
                a.download = `SUNTRONIX_ONE_Asset_Registry_${new Date().toISOString().slice(0,19).replace(/[:T-]/g,"")}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(u);
                toast.success(`Exported ${rows} rows`);
              } catch { toast.error("Could not export"); }
            }} data-testid="asset-export-btn">
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          )}
          <Button variant="outline" onClick={() => {
            const url = process.env.REACT_APP_BACKEND_URL + "/api/assets/bulk/template";
            fetch(url, { credentials: "include" }).then(r => r.blob()).then(b => {
              const u = URL.createObjectURL(b);
              const a = document.createElement("a");
              a.href = u; a.download = "SUNTRONIX_ONE_Device_Bulk_Upload_Template.xlsx";
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(u);
            }).catch(() => toast.error("Could not download template"));
          }} data-testid="asset-template-btn">
            <Download className="h-4 w-4 mr-1.5" /> Download template
          </Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)}
            data-testid="asset-bulk-upload-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Upload Excel
          </Button>
          <Button variant="outline" onClick={() => setNewOpen(true)} data-testid="asset-new-btn">
            <PlusCircle className="h-4 w-4 mr-1.5" /> Add Asset
          </Button>
          <Button onClick={() => navigate("/deploy")} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Rocket className="h-4 w-4 mr-2" strokeWidth={1.6} /> Deploy Device
          </Button>
        </div>
      </div>
      <QuickActionGroup module="assets" />

      {/* Filter bar — inline on md+, bottom sheet on < md */}
      <div className="bg-white border border-slate-200 rounded-md p-3 sticky top-14 z-20">
        {/* Below lg: search + filters trigger */}
        <div className="flex lg:hidden items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <Input
              data-testid={ASSET.searchInput}
              placeholder="Search assets…"
              className="pl-9 h-10"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>
          <MobileFilterSheet
            testId="asset-mobile-filters"
            triggerLabel="Filters"
            activeCount={(lifecycle && lifecycle !== "all" ? 1 : 0) + (condition && condition !== "all" ? 1 : 0)}
            onApply={applyFilters}
            onClear={clearFilters}
          >
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">Lifecycle state</label>
                <Select value={lifecycle} onValueChange={(v) => setLifecycle(v)}>
                  <SelectTrigger data-testid="asset-mobile-filter-lifecycle" className="h-11 w-full"><SelectValue placeholder="Lifecycle state" /></SelectTrigger>
                  <SelectContent>{LIFECYCLE_STATES.map((s) => (<SelectItem key={s} value={s}><span className="capitalize">{s.replace(/_/g, " ")}</span></SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.12em] text-slate-500 mb-1.5">Condition</label>
                <Select value={condition} onValueChange={(v) => setCondition(v)}>
                  <SelectTrigger data-testid="asset-mobile-filter-condition" className="h-11 w-full"><SelectValue placeholder="Condition" /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map((s) => (<SelectItem key={s} value={s}>{s === "all" ? "All conditions" : `Grade ${s}`}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
          </MobileFilterSheet>
        </div>

        {/* Desktop (lg+): inline row */}
        <div className="hidden lg:flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <Input
              data-testid={ASSET.searchInput + "-desktop"}
              placeholder="Search serial, tag, IMEI, model, SKU…"
              className="pl-9 h-9"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>
          <Select value={lifecycle} onValueChange={(v) => setLifecycle(v)}>
            <SelectTrigger data-testid={ASSET.filterLifecycle} className="h-9 w-full sm:w-56"><SelectValue placeholder="Lifecycle state" /></SelectTrigger>
            <SelectContent>{LIFECYCLE_STATES.map((s) => (<SelectItem key={s} value={s}><span className="capitalize">{s.replace(/_/g, " ")}</span></SelectItem>))}</SelectContent>
          </Select>
          <Select value={condition} onValueChange={(v) => setCondition(v)}>
            <SelectTrigger data-testid={ASSET.filterCondition} className="h-9 w-full sm:w-40"><SelectValue placeholder="Condition" /></SelectTrigger>
            <SelectContent>{CONDITIONS.map((s) => (<SelectItem key={s} value={s}>{s === "all" ? "All conditions" : `Grade ${s}`}</SelectItem>))}</SelectContent>
          </Select>
          <Button variant="outline" onClick={applyFilters} className="h-9">
            <Filter className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Apply
          </Button>
          {(q || (lifecycle && lifecycle !== "all") || (condition && condition !== "all")) && (
            <Button variant="ghost" onClick={clearFilters} className="h-9 text-slate-500">
              <X className="h-4 w-4 mr-1.5" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div data-testid="asset-bulk-bar" className="bg-slate-900 text-white rounded-md px-4 py-2.5 flex items-center gap-3 sticky top-28 z-20 animate-fade-in">
          <div className="text-sm">
            <span className="font-semibold tabular-nums">{selected.size}</span> selected
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 hover:text-white" onClick={() => runBulk("reserve")}>Reserve</Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 hover:text-white" onClick={() => runBulk("release")}>Release</Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 hover:text-white" onClick={() => runBulk("unassign")}>
              <Undo2 className="h-4 w-4 mr-1.5" /> Unassign
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 hover:text-white">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>More actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => runBulk("deactivate")} data-testid="asset-bulk-deactivate">
                  <Power className="h-4 w-4 mr-2" /> Deactivate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800 hover:text-white" onClick={() => setSelected(new Set())}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table data-testid={ASSET.table}>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-8">
                <Checkbox
                  checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                  onCheckedChange={toggleAll}
                  data-testid="asset-select-all"
                />
              </TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Asset Tag</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Serial</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Product</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Category</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">State</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Grade</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Country</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Customer</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">ABM ID</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500">Warranty End</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} className="text-sm text-slate-500 py-8 text-center">Loading assets…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="text-sm text-slate-500 py-8 text-center">No assets match the current filters.</TableCell></TableRow>
            ) : rows.map((a) => (
              <TableRow key={a.id} data-testid={ASSET.row(a.id)} className={"cursor-pointer hover:bg-slate-50 " + (selected.has(a.id) ? "bg-slate-50" : "")}>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleRow(a.id)} data-testid={`asset-select-${a.id}`} />
                </TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="font-mono text-xs text-slate-700">{a.asset_tag}</TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="font-mono text-xs text-slate-700">{a.serial}</TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="text-sm text-slate-800">
                  <div className="font-medium">{[a.brand, a.model].filter(Boolean).join(" ") || a.product || "—"}</div>
                  <div className="text-xs text-slate-500">{a.config}</div>
                  {a.brand && (
                    <div className="text-[10px] text-slate-400 uppercase tracking-[0.14em] mt-0.5">
                      {a.brand}
                    </div>
                  )}
                </TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="text-sm text-slate-700">{a.category}</TableCell>
                <TableCell onClick={() => openAsset(a.id)}><StatusBadge value={a.lifecycle_state} /></TableCell>
                <TableCell onClick={() => openAsset(a.id)}><StatusBadge value={a.condition} /></TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="text-sm text-slate-700">{a.country || "—"}</TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="text-sm text-slate-800"
                  data-testid={`asset-customer-${a.id}`}>
                  {(() => {
                    const sub = companies.find((c) => c.id === a.company_id);
                    // Master resolves in this order: asset's own master_company_id →
                    // subsidiary's parent → nothing.  Guarantees a master row is
                    // rendered whenever the data supports it.
                    const master = masters.find((m) =>
                      m.id === a.master_company_id ||
                      (sub && m.id === sub.master_company_id));
                    if (!sub && !master) {
                      return <span className="text-slate-300">—</span>;
                    }
                    return (
                      <div className="leading-tight"
                           data-testid={`asset-customer-cell-${a.id}`}>
                        {master && (
                          <div className="text-[11px] text-slate-500"
                               data-testid={`asset-master-${a.id}`}>
                            <span className="uppercase tracking-wider text-[9px] text-slate-400 mr-1">
                              Master
                            </span>
                            {master.name}
                          </div>
                        )}
                        {sub ? (
                          <div className="font-medium text-slate-800"
                               data-testid={`asset-subsidiary-${a.id}`}>
                            {sub.name}
                          </div>
                        ) : (
                          <div className="text-[11px] text-amber-700 italic">
                            No subsidiary linked
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="font-mono text-[11px] text-slate-700"
                  data-testid={`asset-abm-${a.id}`}>
                  {(() => {
                    const own = a.abm_id;
                    const sub = companies.find((c) => c.id === a.company_id);
                    const master = masters.find((m) =>
                      m.id === a.master_company_id ||
                      (sub && m.id === sub.master_company_id));
                    const abm = own || sub?.abm_id || master?.abm_id;
                    if (!abm) return <span className="text-slate-300">—</span>;
                    const source = own ? "Asset-level ABM ID"
                                    : sub?.abm_id ? `Inherited from ${sub.name}`
                                    : `Inherited from ${master?.name || "master"}`;
                    return (
                      <span title={source}
                        className={own ? "text-slate-800" : "text-slate-500"}>
                        {abm}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell onClick={() => openAsset(a.id)} className="text-xs text-slate-600 font-mono">{a.warranty_end || "—"}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                        data-testid={`asset-actions-${a.id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel className="font-mono text-[10px] text-slate-500">{a.asset_tag}</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openAsset(a.id)} data-testid={`asset-view-${a.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-2" /> View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openAsset(a.id)} data-testid={`asset-edit-${a.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openAsset(a.id)} data-testid={`asset-history-${a.id}`}>
                        <History className="h-3.5 w-3.5 mr-2" /> View History
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {a.active === false ? (
                        <DropdownMenuItem onClick={async () => {
                          try { await api.post(`/assets/${a.id}/status`, { active: true });
                            toast.success(`Asset ${a.asset_tag} activated`); load(); }
                          catch (e) { toast.error(e.response?.data?.detail || "Could not activate"); }
                        }} data-testid={`asset-activate-${a.id}`}>
                          <Power className="h-3.5 w-3.5 mr-2" /> Activate
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={async () => {
                          const reason = window.prompt(`Deactivate asset ${a.asset_tag}?\nOptional reason:`, "");
                          if (reason === null) return;
                          try { await api.post(`/assets/${a.id}/status`, { active: false, reason });
                            toast.success(`Asset ${a.asset_tag} deactivated`); load(); }
                          catch (e) { toast.error(e.response?.data?.detail || "Could not deactivate"); }
                        }} data-testid={`asset-deactivate-${a.id}`}>
                          <Power className="h-3.5 w-3.5 mr-2" /> Deactivate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-700"
                        onClick={async () => {
                          if (!window.confirm(
                            `Delete asset ${a.asset_tag}?\nSerial: ${a.serial}\nThis permanently removes an UNUSED asset record.`
                          )) return;
                          try {
                            await api.delete(`/assets/${a.id}`);
                            toast.success(`Asset ${a.asset_tag} deleted`); load();
                          } catch (e) {
                            const d = e.response?.data?.detail;
                            if (e.response?.status === 409 && d?.dependencies) {
                              const deps = Object.entries(d.dependencies).map(([k, v]) => `${k}: ${v}`).join(", ");
                              toast.error(`Protected — has history (${deps}). Deactivate or correct instead.`, { duration: 6000 });
                            } else {
                              toast.error(typeof d === "string" ? d : "Could not delete");
                            }
                          }
                        }}
                        data-testid={`asset-delete-${a.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete asset
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-500">
            Showing <span className="font-medium text-slate-800">{rows.length ? skip + 1 : 0}</span>–
            <span className="font-medium text-slate-800">{Math.min(skip + rows.length, total)}</span> of{" "}
            <span className="font-medium text-slate-800 tabular-nums">{total.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={skip + rows.length >= total} onClick={() => setSkip(skip + limit)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <AssetDrawer assetId={selectedId} open={drawerOpen} onOpenChange={setDrawerOpen} />
      <CrudDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        title="New asset"
        fields={[
          { name: "asset_tag", label: "Asset tag", required: true, placeholder: "AST-000001" },
          { name: "serial", label: "Serial number", required: true },
          { name: "brand", label: "Brand", required: true, placeholder: "Apple, Dell, Lenovo…" },
          { name: "category", label: "Category", required: true, placeholder: "Laptop, Mobile, Monitor…" },
          { name: "model", label: "Model" },
          { name: "sku", label: "SKU" },
          { name: "config", label: "Config", placeholder: "M4 / 16 GB / 512 GB" },
          { name: "purchase_price", label: "Purchase price", type: "number" },
          { name: "currency", label: "Currency", placeholder: "USD" },
          { name: "purchase_date", label: "Purchase date", type: "date" },
          { name: "warranty_end", label: "Warranty end", type: "date" },
          { name: "master_company_id", label: "Master company", type: "select", required: true, options: masters.map((m) => ({ value: m.id, label: m.name })) },
          { name: "company_id", label: "Client (Subsidiary Company)", type: "select", required: true,
            // Hide entirely when the picked Master has no subsidiaries (e.g. REMOTE),
            // so such assets can be created without a subsidiary.
            hidden: (form) => !!form.master_company_id
              && !companies.some((c) => c.master_company_id === form.master_company_id),
            // Cascading: only show subsidiaries under the picked Master.
            options: (form) => companies
              .filter((c) => !form.master_company_id || c.master_company_id === form.master_company_id)
              .map((c) => ({ value: c.id, label: c.name })),
            // Address auto-fill: pre-fill Country from the master client
            // record when the operator picks a subsidiary (no overwrite if
            // they already typed something in the Country field).
            onSelect: (value, form, set) => {
              const sub = companies.find((c) => c.id === value);
              const parent = sub && masters.find((m) => m.id === sub.master_company_id);
              const country = sub?.country || parent?.country;
              if (country && !form.country) set("country", country);
            },
          },
          { name: "country", label: "Country" },
          { name: "_assignee_pick", label: "Assign to employee (optional)", type: "custom",
            render: ({ form, set, testId }) => (
              <AssigneeTypeahead
                value={form._assignee_label || ""}
                onChange={(v) => { set("_assignee_label", v); if (!v) set("_assignee_id", ""); }}
                onPick={(it) => { if (it.kind === "employee") set("_assignee_id", it.id); else set("_assignee_id", ""); }}
                masterCompanyId={form.master_company_id}
                companyId={form.company_id}
                testId={testId}
                placeholder="Type name · email · code…"
              />
            ),
            // After the asset is created, if an employee was picked, PATCH
            // the new record with the assignment + lifecycle=in_use.
            afterSubmit: async (created, form) => {
              if (!form._assignee_id) return;
              await api.patch(`/assets/${created.id}`, {
                current_employee_id: form._assignee_id,
                lifecycle_state: "in_use",
              });
            },
          },
        ]}
        initial={{}}
        endpoint="/assets/"
        method="POST"
        onSaved={load}
        testIdPrefix="asset-new"
      />
      <BulkAssetUploadDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onDone={load}
      />
    </div>
  );
}
